import { InMemoryCache } from '@apollo/client';
import { createFragmentRegistry } from '@apollo/client/cache';
import dayjs from 'dayjs';
import { disableFragmentWarnings } from 'graphql-tag';

import {
  AttachmentsFragmentDoc,
  DocumentDetailFragmentDoc,
  DraftMessageDetailFragmentDoc,
  FileDetailsFragmentDoc,
  MessageDetailFragmentDoc,
  ProjectDetailFragmentDoc,
  TaskDetailFragmentDoc,
  TaskRowFragmentDoc,
  TeamSubscriptionFragmentDoc,
  UserDetailFragmentDoc,
} from '@graphql/generated';
import { relayStylePagination } from '@hooks/pagination';
import {
  advanceGuardedActivity,
  guardedChatIdFor,
} from '@src/utils/chat/bumpChatToTop';
import { isRenewedSignature } from '@utils/urlExpiry';

import type { Reference, StoreObject, TypePolicies } from '@apollo/client';

// Extracted from initializeCache() so tests can derive keyFields policies
// from the real cache config instead of a second hardcoded copy (TA-10469 —
// see keyFieldsSelectedWhereverTypeAppears.fast.test.ts). Typed explicitly
// as TypePolicies (matching what the inline `new InMemoryCache({ typePolicies
// })` call previously gave for free) so the merge functions' `existing`/
// `incoming` params keep their contextual types instead of falling back to
// implicit `any`.
export const cacheTypePolicies: TypePolicies = {
  Chat: {
    fields: {
      recentActivityAt: {
        // TA-10875: a `listChats` query started before a send can resolve
        // after the local activity bump and write the pre-send timestamp
        // back, dropping the just-sent chat down the list.
        //
        // The guard is deliberately bounded to the race window rather than
        // monotonic: the bumped value comes from the device clock, and
        // `recentActivityAt` legitimately decreases server-side (deleting a
        // chat's newest message). An unbounded ratchet would pin a chat with
        // a wrong timestamp forever — the cache is persisted, so it would
        // survive restarts with no corrective path left.
        merge(existing: string | null | undefined, incoming: string | null) {
          // Guard only a value this client bumped, and only while the race
          // window is open — every other write is authoritative. The lookup
          // also disarms itself when two chats share the value, so it can
          // never pin the wrong chat (guardedChatIdFor).
          const guardedChatId = existing
            ? guardedChatIdFor(existing)
            : undefined;
          if (!guardedChatId) return incoming;

          // Inside the window an absent or older value is the losing side of
          // the race. dayjs, matching the chatUpdate guard (useChatInput) and
          // chatSort — Date.parse yields NaN for a malformed timestamp, and
          // `NaN < existing` is false, so it would let junk through.
          if (
            !incoming ||
            !dayjs(incoming).isValid() ||
            dayjs(incoming).isBefore(dayjs(existing))
          ) {
            return existing;
          }

          // Newer value wins, and the guard follows it for the rest of the
          // window so a slower straggler from before the send cannot undo it.
          advanceGuardedActivity(existing as string, incoming);
          return incoming;
        },
      },
    },
  },
  User: {
    fields: {
      onboardingState: {
        merge(existing, incoming) {
          return { ...existing, ...incoming };
        },
      },
      unviewedProjectAssignments: {
        // Guard: the globally-mounted meUpdatedOnboarding subscription
        // (useProjectOnboarding) writes this field on every meUpdated event
        // without selecting the parent User id, so Apollo cannot normalize
        // the update and would orphan the existing normalized Project refs —
        // firing invariant #89 ("cache data may be lost") through the
        // notifySubscription path on web (TA-10061). Declaring an explicit
        // merge policy silences the warning: use the server's authoritative
        // list when present, keep the cached refs when the field is absent.
        merge: (
          existing: { __ref: string }[] | null | undefined,
          incoming: { __ref: string }[] | null | undefined
        ) => incoming ?? existing,
      },
    },
  },
  Document: {
    keyFields: ['clientId'],
  },
  File: {
    fields: {
      thumbnail: {
        // Guard 1 (TA-10290): `userAvatarsByIds`/`chatAvatarsByIds`/
        // `teamAvatarsByIds` validation refetches can return
        // `thumbnail: null` for an id whose thumbnail was previously
        // cached and valid. Keep the cached thumbnail when incoming is
        // null/empty/whitespace; a later authoritative non-empty value
        // still replaces it normally.
        //
        // Guard 2 (TA-10111): S3 presigned URLs rotate the query-string
        // signature on every server response (subscriptions fire on every
        // read-receipt/reaction update) even though the image itself has
        // not changed. Since `File` is normalized, each write creates a
        // new object reference, which invalidates every watcher reading
        // that entity — including GetChatQuery — and triggers a re-render
        // cascade that causes visible avatar reloads. Compare only the
        // stable path component (before `?`); if it matches the cached
        // URL's path, the image is the same and we return the existing
        // URL so Apollo sees no change and fires no watchers.
        merge: (
          existing: string | null | undefined,
          incoming: string | null | undefined
        ) => {
          const trimmed = incoming?.trim();
          if (!trimmed) return existing ?? incoming;
          if (existing) {
            const existingBase = existing.split('?')[0];
            const incomingBase = trimmed.split('?')[0];
            if (existingBase && existingBase === incomingBase) {
              // TA-10939: same path is not the same signature. Once the held
              // URL has expired, keeping it would pin a dead URL forever —
              // Apollo hands the *merged* value back to the caller even for
              // network-only queries, so a renewed signature would never reach
              // the avatar, the persisted timestamp, or the remount key.
              return isRenewedSignature(existing, trimmed) ? trimmed : existing;
            }
          }
          return trimmed;
        },
      },
    },
  },
  DraftMessage: {
    keyFields: ['chatId'],
  },
  MessageReaction: {
    fields: {
      user: {
        // Guard: return existing when the field is not selected in the
        // query so we don't orphan normalized User refs in the cache
        // and trigger Apollo invariant #89.
        merge(existing, incoming) {
          return incoming ?? existing;
        },
      },
    },
  },
  Message: {
    keyFields: ['clientId'],
    fields: {
      reaction: {
        // Guard: preserve existing MessageReaction refs when the field is
        // absent from the response so normalized objects are not orphaned.
        merge: (
          existing: { __ref: string }[] | undefined,
          incoming: { __ref: string }[] | undefined
        ) => incoming ?? existing,
      },
      attachments: {
        // Guard: use the server's authoritative list when present; keep
        // existing when the field is not selected. The previous filter
        // that removed MessageTask/MessageProject refs from `existing`
        // was orphaning those normalized objects and triggering #89.
        //
        // TA-10217: an incoming list that has NO Document ref never drops
        // Document refs already cached, when existing had any. A
        // message's Document (image/file) attachments are attached to it
        // server-side asynchronously (see the Sidekiq job referenced in
        // insertPublishedMessageEdge's TA-10162 comment) — a read that
        // lands before that job finishes (the messageUpdate subscription's
        // lite payload, or the reverse-pagination catch-up fetch added in
        // TA-10150, both of which can fire right as the user returns to a
        // chat mid-send) looks authoritative but is genuinely incomplete.
        // This affects a plain empty array (`[]`) equally with a
        // non-empty-but-Document-less one (e.g. `[MessageTask]`, when a
        // Document is sent alongside a task/project tag — the tag attaches
        // synchronously, the Document doesn't) — both would otherwise wipe
        // the image grid for several seconds until a later write
        // repopulates it (QA caught the tag-mixed case in TA-10217).
        //
        // Safe against legitimate deletion: deleteMessageDocument (see
        // MediaViewer.web.tsx) only fires when the message has more than
        // one Document, so its listMessages refetch always leaves at
        // least one Document ref in `incoming` — "incoming has zero
        // Document refs" can never be a legitimate deletion outcome for a
        // message that still exists (deleting the last image deletes the
        // whole message instead, a separate evict/delete path this merge
        // never sees). MessageTask/MessageProject tags are unaffected:
        // updateMessageTags can still legitimately clear all tags to `[]`
        // when existing had no Document.
        merge: (
          existing: { __ref: string }[] | undefined,
          incoming: { __ref: string }[] | null | undefined,
          {
            readField,
          }: {
            readField: <T = unknown>(
              fieldName: string,
              from: Reference
            ) => T | undefined;
          }
        ) => {
          // attachments is nullable (Maybe<Array<Attachment>>) — a
          // server response can send an explicit `null`, not just omit
          // the field (undefined). Both must fall back to `existing`.
          if (incoming == null) return existing;
          const isDocument = (ref: { __ref: string }) =>
            readField('__typename', ref) === 'Document';
          if (incoming.some(isDocument)) return incoming;
          const existingDocuments = (existing ?? []).filter(isDocument);
          if (existingDocuments.length === 0) return incoming;
          return [...incoming, ...existingDocuments];
        },
      },
    },
  },
  SubscriptionDetails: {
    // TA-10633: SubscriptionDetails has no `id`, so it is stored as an
    // embedded object under its Team and a write REPLACES it wholesale.
    // Fragments disagree about how much of it they select — projectDetail
    // and taskDetail take six fields, while attachments/messageDetail/
    // messageUpdateLite take only `expirationDate` + `subscriptionStatus`.
    // So any chat traffic (the web chat rail is always mounted) overwrote the
    // task's cached subscriptionDetails with the two-field version and
    // dropped `paymentCardLast4` & co — the "Cache data may be lost when
    // replacing the subscriptionDetails field of a Team object" warning in
    // the TA-10633 Jam recording at t=23.4s.
    //
    // The damage is not cosmetic: TaskDetail selects those fields, so
    // `useGetTasksFromCache`'s readFragment then returned null, the assignee
    // modal saw zero tasks, and the remove-member handler iterated an empty
    // list — no mutation, no "Assignee Deleted" toast, member still there
    // after a refresh. Shallow-merging keeps the wider cached fields; a query
    // that does select them still overwrites them normally.
    //
    // Trade-off, deliberate: a field that used to be *deleted* by a narrow
    // write is now *stale but present*, so a cache-first query selecting it is
    // satisfied from the cache instead of missing and refetching. In practice
    // the subscription-sensitive reads stay fresh because they go to the
    // network on their own — useTeamFromQuery, useListTeamsFromQuery and
    // useTeamBillingFromQuery are all cache-and-network, and the one
    // cache-first path (useProjectFromQuery) selects the wide set via
    // projectDetailFragment, so it refreshes all six fields whenever it does
    // fetch. The lasting fix is to make the narrow fragments select the same
    // six fields; until then do not add a cache-only read of
    // `paymentCardLast4` / `isSubscription*` and assume it is current.
    merge: true,
  },
  Task: {
    fields: {
      // TA-10633: `updateTask` replaces `members` wholesale, so a removal
      // writes a shorter list over the cached one. Apollo cannot tell that
      // from data loss and logs "Cache data may be lost when replacing the
      // members field of a Task object" on every remove.
      //
      // Declaring any merge function suppresses that warning, and taking
      // `incoming` is exactly the default behaviour — the server's list is
      // authoritative and a removed member must not come back. So this policy
      // silences the warning and changes nothing else; that is its whole job.
      //
      // Deliberately NOT extended to `invitations` / `userRequests`: a field
      // that is absent from a write is already preserved by the cache, and a
      // field's merge function is not even invoked in that case, so a policy
      // there would buy nothing. It would only add an `incoming ?? existing`
      // null branch, and since both are nullable (`[TaskInvitation]` /
      // `[UserRequest]`, unlike the non-null `[TaskMember!]!`) that branch
      // would resurrect invitations the server had just reported as gone.
      members: {
        merge: (
          _existing: { __ref: string }[] | undefined,
          incoming: { __ref: string }[]
        ) => incoming,
      },
    },
  },
  Query: {
    fields: {
      listMessages: {
        ...relayStylePagination(),
        keyArgs: ['chatId', 'includeDraft', 'includePublished'],
        merge(
          existing,
          incoming,
          {
            readField,
            args,
          }: {
            readField: <T = unknown>(
              fieldName: string,
              from: Reference | StoreObject | undefined
            ) => T | undefined;
            args: Record<string, unknown> | null;
          }
        ) {
          const combineArrays = (
            existing: { node: { __ref: unknown } }[] = [],
            incoming: { node: { __ref: unknown } }[] = []
          ) => {
            const incomingRefs = new Set(incoming.map((e) => e.node.__ref));
            const incomingClientIds = new Set(
              incoming
                .map((e) => readField<string>('clientId', e.node))
                .filter((id): id is string => !!id)
            );
            const filtered = existing.filter(({ node }) => {
              if (incomingRefs.has(node.__ref)) return false;
              const clientId = readField<string>('clientId', node);
              return !(clientId && incomingClientIds.has(clientId));
            });
            return [...incoming, ...filtered];
          };
          const mergedEdges = combineArrays(
            existing?.edges || [],
            incoming?.edges || []
          );
          if (args !== null && 'before' in args && args.last != null) {
            // Reverse pagination (fetching newer messages via last/before):
            // Guard on `last` as well so a bare `before: ''` (e.g. from
            // notification-driven refetches) doesn't falsely enter this branch.
            // incoming.pageInfo reflects only the new batch, so preserve the
            // existing endCursor/hasNextPage so forward pagination (older
            // messages) continues to work correctly.
            return {
              ...incoming,
              pageInfo: {
                ...incoming?.pageInfo,
                endCursor:
                  existing?.pageInfo?.endCursor ??
                  incoming?.pageInfo?.endCursor,
                hasNextPage:
                  existing?.pageInfo?.hasNextPage ??
                  incoming?.pageInfo?.hasNextPage,
              },
              edges: mergedEdges,
            };
          }
          const isForwardPagination =
            args !== null && 'after' in args && args.after != null;
          if (!isForwardPagination && existing?.pageInfo) {
            // Bare page-1 fetch (plain refetch(), no after/before): this
            // happens whenever ChatMessageData's fetchNewerMessages falls
            // back to refetch() (no real cursor to anchor on, or the
            // reverse-pagination fetchMore rejected — TA-10379) as well as
            // pull-to-refresh's onRefresh(). incoming.pageInfo only
            // describes page 1, so — same as the reverse-pagination branch
            // above — preserve the deeper endCursor/hasNextPage a user who
            // has paginated into older history already reached, instead of
            // snapping pagination back to page 1.
            return {
              ...incoming,
              pageInfo: {
                ...incoming?.pageInfo,
                endCursor:
                  existing.pageInfo.endCursor ?? incoming?.pageInfo?.endCursor,
                hasNextPage:
                  existing.pageInfo.hasNextPage ??
                  incoming?.pageInfo?.hasNextPage,
              },
              edges: mergedEdges,
            };
          }
          return {
            ...incoming,
            edges: mergedEdges,
          };
        },
      },
      listTasks: {
        ...relayStylePagination(),
        // TA-10502 (QA fix): every arg that changes *which* tasks the server
        // returns has to be part of the field's cache identity, otherwise two
        // differently-filtered queries resolve to the same cache field and the
        // last write wins. `chatId`, `excludeCompleted`, `collaboratorsOnly`
        // and `withoutPhase` were missing — the tag pickers batch with
        // `excludeCompleted: true` (TagModal.web) and `undefined`
        // (MyTagModal.web) over the same project ids, so one picker could
        // render the other's filtered result.
        keyArgs: [
          'projectIds',
          'memberIds',
          'teamIds',
          'chatId',
          'sortBy',
          'status',
          'userId',
          'dueGroup',
          'taskCollaborators',
          'collaboratorsOnly',
          'excludeCompleted',
          'priority',
          'dueDateBegin',
          'dueDateEnd',
          'withoutDueDate',
          'withoutPhase',
        ],
      },
      listMyTasksProjectGroups: {
        ...relayStylePagination(),
        // TA-10373 added groupBy (PROJECT/PRIORITY/ASSIGNEE) plus several
        // other filter args to this query without updating keyArgs — any
        // arg missing here isn't part of the field's cache identity, so
        // switching e.g. groupBy alone looked like "the same field,
        // paginate/merge" to Apollo and served back stale
        // differently-shaped groups (surfaced as every group rendering
        // "Low Priority": composeServerGroups fell back to Low when the
        // stale nodes' `priority` field was absent).
        keyArgs: [
          'projectIds',
          'memberIds',
          'priority',
          'dueDateBegin',
          'dueDateEnd',
          'taskCollaborators',
          'dueGroup',
          'excludeCompleted',
          'groupBy',
          'requireDueDate',
          'sortBy',
          'status',
          'teamIds',
          'userId',
          'collaboratorsOnly',
          'withoutPhase',
          'phaseId',
        ],
      },
      listProjects: {
        ...relayStylePagination(),
        keyArgs: [
          'filterTeamsProjects',
          'archivesOnly',
          'locationOnly',
          'chatId',
          'userId',
          'sortBy',
          'role',
          'ownerIds',
          'memberIds',
          'teamIds',
        ],
      },
      listActivitiesByTask: {
        ...relayStylePagination(),
        keyArgs: ['taskId'],
      },
      listActivitiesByProject: {
        ...relayStylePagination(),
        keyArgs: ['projectId'],
      },
      listActivities: {
        ...relayStylePagination(),
        keyArgs: ['userIds', 'teamIds', 'startDate', 'endDate'],
      },
      listChatsPagination: {
        ...relayStylePagination(),
        keyArgs: ['archivesOnly'],
      },
      listRecentProjectsTasks: {
        keyArgs: ['id', 'chatId', 'excludeCompleted', 'collaboratorsOnly'],
      },
      listProjectsRecent: {
        keyArgs: ['id'],
      },
    },
  },
};

export const initializeCache = () => {
  const fragmentRegistry = createFragmentRegistry(
    FileDetailsFragmentDoc,
    AttachmentsFragmentDoc,
    MessageDetailFragmentDoc,
    DraftMessageDetailFragmentDoc,
    UserDetailFragmentDoc,
    ProjectDetailFragmentDoc,
    TaskDetailFragmentDoc,
    TaskRowFragmentDoc,
    TeamSubscriptionFragmentDoc,
    DocumentDetailFragmentDoc
  );

  if (process.env.NODE_ENV === 'test') {
    disableFragmentWarnings();
  }

  return new InMemoryCache({
    fragments: fragmentRegistry,
    possibleTypes: {
      Attachment: [
        'Document',
        'EntityUser',
        'Folder',
        'LocalFile',
        'Message',
        'MessageProject',
        'MessageTask',
        'MessageTaskAssignment',
        'MessageTaskComplete',
        'Project',
        'ProjectUser',
        'PublicTeam',
        'Task',
        'TaskUser',
        'UserRequest',
      ],
      ProjectOrPublicProject: ['Project', 'PublicProject'],
    },
    typePolicies: cacheTypePolicies,
  });
};
